import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckinGuideConfig } from '../api.checkinGuide';
import { GuideView } from '../pages/CheckinGuidePage';
import { buildPreviewGuide } from '../utils/guidePreview';
import { guidePreviewText } from '../i18n/pages/guidePreview';
import { useLang } from '../i18n/LanguageContext';

interface Props {
  /** The editor's current draft, saved or not. Read-only here: closing never changes it. */
  config: CheckinGuideConfig;
  apartmentName: string;
  onClose: () => void;
}

// `summary` is the native control of the "differs from the real guide" disclosure; without it a
// keyboard user could never open that explanation (review F1).
const FOCUSABLE = 'a[href], summary, button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Full-screen preview of the guest Guide for the manager (#146). It renders the same `GuideView`
 * the guest sees, fed by `buildPreviewGuide` instead of the public endpoint, so it makes no request
 * at all. It owns no editor state: opening and closing cannot touch the draft, the dirty flag or
 * Save/Discard.
 */
const GuidePreviewModal: React.FC<Props> = ({ config, apartmentName, onClose }) => {
  const { lang } = useLang();
  const p = guidePreviewText[lang];
  const [previewLang, setPreviewLang] = useState<'cs' | 'en'>(lang === 'cs' ? 'cs' : 'en');
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const guide = useMemo(
    () => buildPreviewGuide(config, apartmentName, previewLang),
    [config, apartmentName, previewLang],
  );

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const items = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
        .filter(el => el.offsetParent !== null || el === document.activeElement);
      if (items.length === 0) return;
      // The trap owns Tab entirely: one ordered list, wrapping at both ends, so the disclosure
      // (first in DOM order) is reachable in both directions and focus never leaves the dialog.
      const active = document.activeElement as HTMLElement | null;
      const idx = active ? items.indexOf(active) : -1;
      const next = e.shiftKey
        ? (idx > 0 ? items[idx - 1] : items[items.length - 1])
        : (idx >= 0 && idx < items.length - 1 ? items[idx + 1] : items[0]);
      e.preventDefault();
      next.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      opener?.focus?.();
    };
    // onClose is the parent's setState wrapper; re-binding on identity change would steal focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={p.dialogTitle}
      style={{ position: 'fixed', inset: 0, zIndex: 2000, background: '#f1f5f9', display: 'flex', flexDirection: 'column' }}
    >
      <div style={{ flex: '0 0 auto', background: '#fffbeb', borderBottom: '1px solid #fcd34d', padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: '0.82rem', color: '#78350f', lineHeight: 1.45 }}>
          <strong>{p.bannerTitle}.</strong> {p.bannerBody}
          <details style={{ marginTop: 4 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{p.bannerDiffsLabel}</summary>
            <div style={{ marginTop: 4 }}>{p.bannerDiffs}</div>
          </details>
        </div>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          style={{ flex: '0 0 auto', minHeight: 44, minWidth: 44, padding: '0 14px', border: '1px solid #d97706', borderRadius: 8, background: '#fff', color: '#78350f', fontWeight: 700, cursor: 'pointer' }}
        >
          ✕ {p.close}
        </button>
      </div>
      <div data-testid="guide-preview-scroll" style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <GuideView guide={guide} language={previewLang} setLanguage={setPreviewLang} preview />
      </div>
    </div>
  );
};

export default GuidePreviewModal;
